import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { api } from '../services/api';
import {
  TrendingUp,
  MessageCircle,
  Bot,
  User,
  Clock,
  CheckCircle,
  BarChart3,
  AlertCircle,
  Target,
  Download,
  FileSpreadsheet,
} from 'lucide-react';
import type { AnalyticsOverview, AgentPerformance } from '../types';

export default function Analytics() {
  const { t } = useTranslation();
  const [overview, setOverview] = useState<AnalyticsOverview | null>(null);
  const [agentPerformance, setAgentPerformance] = useState<AgentPerformance[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadAnalytics();
  }, []);

  const loadAnalytics = async () => {
    try {
      const [overviewData, performanceData] = await Promise.all([
        api.getAnalyticsOverview(),
        api.getAgentPerformance(),
      ]);
      setOverview(overviewData);
      setAgentPerformance(performanceData);
    } catch (error) {
      console.error('Failed to load analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async (type: 'conversations' | 'agents') => {
    try {
      const tokens = localStorage.getItem('auth_tokens');
      if (!tokens) return;

      const { accessToken } = JSON.parse(tokens);
      const API_URL = (import.meta as any).env?.VITE_API_URL || 'https://app.tahc.ai';

      const response = await fetch(`${API_URL}/api/analytics/export/csv?type=${type}`, {
        headers: {
          'Authorization': `Bearer ${accessToken}`,
        },
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}-${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        document.body.removeChild(a);
      } else {
        console.error('Export failed');
      }
    } catch (error) {
      console.error('Failed to export:', error);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-gray-500">{t('analytics.loadingAnalytics')}</div>
      </div>
    );
  }

  const stats = [
    {
      name: t('analytics.totalConversations'),
      value: overview?.total_conversations || 0,
      icon: MessageCircle,
      color: 'bg-blue-500',
      textColor: 'text-blue-600',
      bgColor: 'bg-blue-50',
    },
    {
      name: t('analytics.aiHandled'),
      value: overview?.ai_handled || 0,
      icon: Bot,
      color: 'bg-green-500',
      textColor: 'text-green-600',
      bgColor: 'bg-green-50',
      percentage: overview?.total_conversations
        ? Math.round(((overview.ai_handled || 0) / overview.total_conversations) * 100)
        : 0,
    },
    {
      name: t('analytics.humanHandled'),
      value: overview?.human_handled || 0,
      icon: User,
      color: 'bg-gray-500',
      textColor: 'text-gray-600',
      bgColor: 'bg-gray-50',
      percentage: overview?.total_conversations
        ? Math.round(((overview.human_handled || 0) / overview.total_conversations) * 100)
        : 0,
    },
    {
      name: t('analytics.aiSuggested'),
      value: overview?.ai_suggested || 0,
      icon: AlertCircle,
      color: 'bg-yellow-500',
      textColor: 'text-yellow-600',
      bgColor: 'bg-yellow-50',
    },
    {
      name: t('analytics.escalationRate'),
      value: `${overview?.escalation_rate || 0}%`,
      icon: TrendingUp,
      color: 'bg-orange-500',
      textColor: 'text-orange-600',
      bgColor: 'bg-orange-50',
    },
    {
      name: t('analytics.aiAccuracy'),
      value: `${overview?.ai_accuracy_rate || 0}%`,
      icon: Target,
      color: 'bg-black',
      textColor: 'text-black',
      bgColor: 'bg-gray-100',
    },
    {
      name: t('analytics.avgResponseTime'),
      value: `${Math.round((overview?.average_response_time || 0) / 60)}m`,
      icon: Clock,
      color: 'bg-pink-500',
      textColor: 'text-pink-600',
      bgColor: 'bg-pink-50',
    },
    {
      name: t('analytics.openConversations'),
      value: overview?.open_conversations || 0,
      icon: CheckCircle,
      color: 'bg-teal-500',
      textColor: 'text-teal-600',
      bgColor: 'bg-teal-50',
    },
  ];

  return (
    <div className="h-full overflow-y-auto bg-gray-50">
      <div className="p-4 sm:p-6 lg:p-8">
        {/* Header */}
        <div className="mb-6 sm:mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div className="flex items-center gap-3">
              <BarChart3 className="w-6 h-6 text-gray-900" />
              <div>
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{t('analytics.title')}</h1>
                <p className="text-sm sm:text-base text-gray-600">{t('analytics.subtitle')}</p>
              </div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleExport('conversations')}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition text-sm sm:text-base"
              >
                <Download className="w-4 h-4" />
                <span className="hidden sm:inline">{t('analytics.exportConversations')}</span>
                <span className="sm:hidden">{t('analytics.conversations')}</span>
              </button>
              <button
                onClick={() => handleExport('agents')}
                className="flex items-center gap-2 px-3 sm:px-4 py-2 bg-black text-white rounded-lg hover:bg-gray-800 transition text-sm sm:text-base"
              >
                <FileSpreadsheet className="w-4 h-4" />
                <span className="hidden sm:inline">{t('analytics.exportAgents')}</span>
                <span className="sm:hidden">{t('analytics.agents')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
          {stats.map((stat) => (
            <div key={stat.name} className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100">
              <div className="flex items-center justify-between mb-3 sm:mb-4">
                <div className={`p-2 sm:p-3 rounded-lg ${stat.bgColor}`}>
                  <stat.icon className={`w-5 h-5 sm:w-6 sm:h-6 ${stat.textColor}`} />
                </div>
                {stat.percentage !== undefined && (
                  <span className="text-xs sm:text-sm font-medium text-gray-600">{stat.percentage}%</span>
                )}
              </div>
              <h3 className="text-xl sm:text-2xl font-bold text-gray-900 mb-1">{stat.value}</h3>
              <p className="text-xs sm:text-sm text-gray-600">{stat.name}</p>
            </div>
          ))}
        </div>

        {/* AI Performance Insights */}
        <div className="bg-white rounded-xl shadow-sm p-4 sm:p-6 border border-gray-100 mb-6 sm:mb-8">
          <h2 className="text-lg sm:text-xl font-semibold text-gray-900 mb-4 sm:mb-6">{t('analytics.performanceInsights')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 sm:gap-6">
            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-green-50 rounded-lg">
                  <Bot className="w-5 h-5 text-green-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('analytics.aiDeflectionRate')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {overview?.total_conversations
                      ? Math.round(((overview.ai_handled || 0) / overview.total_conversations) * 100)
                      : 0}
                    %
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500">{t('analytics.aiDeflectionDescription')}</p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-yellow-50 rounded-lg">
                  <AlertCircle className="w-5 h-5 text-yellow-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('analytics.aiAssistanceRate')}</p>
                  <p className="text-2xl font-bold text-gray-900">
                    {overview?.total_conversations
                      ? Math.round(((overview.ai_suggested || 0) / overview.total_conversations) * 100)
                      : 0}
                    %
                  </p>
                </div>
              </div>
              <p className="text-xs text-gray-500">{t('analytics.aiAssistanceDescription')}</p>
            </div>

            <div>
              <div className="flex items-center gap-3 mb-3">
                <div className="p-2 bg-gray-100 rounded-lg">
                  <Target className="w-5 h-5 text-black" />
                </div>
                <div>
                  <p className="text-sm text-gray-600">{t('analytics.aiAccuracy')}</p>
                  <p className="text-2xl font-bold text-gray-900">{overview?.ai_accuracy_rate || 0}%</p>
                </div>
              </div>
              <p className="text-xs text-gray-500">{t('analytics.aiAccuracyDescription')}</p>
            </div>
          </div>
        </div>

        {/* Agent Performance Table */}
        {agentPerformance.length > 0 && (
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-4 sm:p-6 border-b border-gray-100">
              <h2 className="text-lg sm:text-xl font-semibold text-gray-900">{t('analytics.agentPerformance')}</h2>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('analytics.agent')}
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                      {t('analytics.conversations')}
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden sm:table-cell">
                      {t('analytics.avgResponseTime')}
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      {t('analytics.aiApprovals')}
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      {t('analytics.aiEdits')}
                    </th>
                    <th className="px-4 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider hidden md:table-cell">
                      {t('analytics.aiRejections')}
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {agentPerformance.map((agent) => (
                    <tr key={agent.agent_id} className="hover:bg-gray-50">
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-3">
                            <User className="w-4 h-4 text-gray-600" />
                          </div>
                          <div className="text-sm font-medium text-gray-900">{agent.agent_name}</div>
                        </div>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {agent.conversations_handled}
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900 hidden sm:table-cell">
                        {Math.round(agent.average_response_time / 60)}m
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-800">
                          {agent.ai_approvals}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <span className="px-2 py-1 text-xs rounded-full bg-blue-100 text-blue-800">
                          {agent.ai_edits}
                        </span>
                      </td>
                      <td className="px-4 sm:px-6 py-4 whitespace-nowrap hidden md:table-cell">
                        <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-800">
                          {agent.ai_rejections}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
